import { IntegrationRouteConfig, IntegrationRouteInit } from './integrationRouteConfig';
import { IntegrationRoute, IntegrationRouteEnhancer } from './integrationRouteInterface';
import {
    IntegrationContainerBaseConfig,
    IntegrationControllerBaseConfig,
    IntegrationLoaderBaseConfig,
    IntegrationRouteNavigation,
    IntegrationViewBaseConfig,
    IntegrationRouteInfra,
} from './base';
import {
    createThreadDetailContainer,
    createThreadDetailLoader,
    createThreadDetailController,
    createCallOverlayContainer,
    createCallOverlayController,
    createCallOverlayView,
    createCallUpsertContainer,
    createCallUpsertController,
    createCallUpsertLoader,
    createThreadUpsertRoute,
    createThreadUpsertLoader,
    createThreadUpsertContainer,
    createThreadUpsertController,
    createThreadNewLoader,
    createThreadNewContainer,
    createThreadNewController,
    createThreadCommonView,
    createCallUpsertView,
} from './page';
import { assert } from '@varos/util-typescript';

export function createIntegrationRoute(
    config: IntegrationRouteInit,
    enhancer?: IntegrationRouteEnhancer
) {
    return create({ ...config }, enhancer);
}

function create(
    config: IntegrationRouteConfig,
    enhancer?: IntegrationRouteEnhancer
): IntegrationRoute {
    if (enhancer) {
        return enhancer(create)(config);
    }
    const {
        route: {
            chat: {
                Container: {
                    Thread: {
                        Option: ThreadOptionContainer,
                        Context: {
                            Select: ThreadContextSelectContainer,
                            Item: ThreadContextItemContainer,
                            Collection: CollectionContextContainer,
                            Solution: SolutionContextContainer,
                        },
                    },
                    Message: {
                        Context: { Item: MessageContextItemContainer },
                    },
                    Composer: ComposerContainer,
                },
            },
        },
    } = config;

    // Create all routers needed for the application
    const reportDetailRouter = config.kernel.provider.createRouter({
        state: {
            kind: 'hash',
        },
    });

    // Create a router map to be used throughout the app
    const router = {
        report: reportDetailRouter,
    };

    const infra: IntegrationRouteInfra = {};

    const navigation: IntegrationRouteNavigation = {};

    const containerConfig: IntegrationContainerBaseConfig = {
        context: {
            ...config.context,
            chat: config.route.chat.context.chat,
        },
        kernel: config.kernel,
        UI: config.UI,
        router,
        repository: config.repository,
        navigation,
        external: config.external,
        infra,
        state: config.state,
        hook: config.hook,
    };

    const loaderConfig: IntegrationLoaderBaseConfig = {
        repository: config.repository,
    };

    const controllerConfig: IntegrationControllerBaseConfig = {
        navigation,
        controller: {
            ...config.controller,
            thread: {
                detail: config.route.chat.controller.thread.detail,
            },
        },
        kernel: config.kernel,
        infra,
    };

    const viewConfig: IntegrationViewBaseConfig = {
        infra,
        UI: config.UI,
        Layout: config.Layout,
        router,
        kernel: config.kernel,
    };

    // Create CallUpsertContainer
    const Call = {
        Overlay: createCallOverlayContainer(
            containerConfig,
            createCallOverlayController(controllerConfig),
            createCallOverlayView({
                ...viewConfig,
                Container: {
                    Call: {
                        Upsert: createCallUpsertContainer(
                            containerConfig,
                            createCallUpsertLoader(loaderConfig),
                            createCallUpsertController(controllerConfig),
                            createCallUpsertView(viewConfig)
                        ),
                    },
                },
            })
        ),
    };

    const Thread = {
        Route: createThreadUpsertRoute({
            ...containerConfig,
            keys: {
                integrationUrlParam: 'integrationId',
            },
            Container: createThreadUpsertContainer(
                {
                    ...containerConfig,
                    Container: {
                        New: createThreadNewContainer(
                            containerConfig,
                            createThreadNewLoader(loaderConfig),
                            createThreadNewController(controllerConfig),
                            createThreadCommonView({
                                ...viewConfig,
                                Container: {
                                    Call: {
                                        Request: {
                                            Overlay:
                                                config.route.chat.Container.Call.Request
                                                    .Overlay,
                                        },
                                    },
                                    CallOverlay: Call.Overlay,
                                    ThreadOption: ThreadOptionContainer,
                                    ThreadContext: ThreadContextSelectContainer,
                                    Context: {
                                        Select: {
                                            Collection: CollectionContextContainer,
                                            Solution: SolutionContextContainer,
                                        },
                                        Item: ThreadContextItemContainer,
                                    },
                                    Composer: ComposerContainer,
                                    Message: {
                                        Item: config.route.chat.Container.Message.Item,
                                        Group: config.route.chat.Container.Message.Group,
                                        Section:
                                            config.route.chat.Container.Message.Section,
                                        Part: config.route.chat.Container.Message.Part,
                                        Context: {
                                            Item: MessageContextItemContainer,
                                        },
                                    },
                                },
                            })
                        ),
                        Detail: createThreadDetailContainer(
                            containerConfig,
                            createThreadDetailLoader(loaderConfig),
                            createThreadDetailController(controllerConfig),
                            createThreadCommonView({
                                ...viewConfig,
                                Container: {
                                    Call: {
                                        Request: {
                                            Overlay:
                                                config.route.chat.Container.Call.Request
                                                    .Overlay,
                                        },
                                    },
                                    CallOverlay: Call.Overlay,
                                    ThreadOption: ThreadOptionContainer,
                                    ThreadContext: ThreadContextSelectContainer,
                                    Context: {
                                        Select: {
                                            Collection: CollectionContextContainer,
                                            Solution: SolutionContextContainer,
                                        },
                                        Item: ThreadContextItemContainer,
                                    },
                                    Composer: ComposerContainer,
                                    Message: {
                                        Item: config.route.chat.Container.Message.Item,
                                        Group: config.route.chat.Container.Message.Group,
                                        Section:
                                            config.route.chat.Container.Message.Section,
                                        Part: config.route.chat.Container.Message.Part,
                                        Context: {
                                            Item: MessageContextItemContainer,
                                        },
                                    },
                                },
                            })
                        ),
                    },
                },
                createThreadUpsertLoader(loaderConfig),
                createThreadUpsertController(controllerConfig)
            ),
        }),
    };

    return {
        Pages: {
            Integration: {
                Detail: Thread.Route,
            },
        },
    };
}
